import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ScreenHeader from '@/components/ui/ScreenHeader';
import AppScreen from '@/components/ui/AppScreen';
import Card from '@/components/ui/Card';
import PrimaryButton from '@/components/ui/PrimaryButton';
import SecondaryButton from '@/components/ui/SecondaryButton';
import CardProximoEvento from '@/pages/home/components/CardProximoEvento';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import { suportaAvisos, ligarAvisosGerais, precisaInstalarNoIphone } from '@/lib/avisos';
import { situacaoLiturgia, definirLiturgia, type SituacaoLiturgia } from '@/lib/liturgia';

const LITURGIA_CNBB = 'https://liturgiadiaria.edicoescnbb.com.br/';

interface EventoProximo {
  id: number;
  nome: string;
  slug: string;
  inicioEm: string;
  capa: string | null;
}

interface EventoLido {
  id: number;
  nome: string;
  slug: string;
  inicio_em: string;
  fim_em: string | null;
  imagem_mobile_url: string | null;
  imagem_desktop_url: string | null;
}

function dataLonga(d: Date = new Date()): string {
  const s = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function faltamDias(iso: string): number {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const alvo = new Date(iso);
  alvo.setHours(0, 0, 0, 0);
  return Math.round((alvo.getTime() - hoje.getTime()) / 86400000);
}

function textoFaltam(iso: string): string {
  const dias = faltamDias(iso);
  if (dias <= 0) return 'É hoje!';
  if (dias === 1) return 'Falta 1 dia';
  return `Faltam ${dias} dias`;
}

export default function LiturgiaPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [sit, setSit] = useState<SituacaoLiturgia>({ assinado: false, ativa: false, liturgia: false });
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [proximos, setProximos] = useState<EventoProximo[]>([]);
  const [carregandoEventos, setCarregandoEventos] = useState(false);

  const noIphoneSemApp = precisaInstalarNoIphone();
  const pushOk = suportaAvisos();

  const recarregarSituacao = useCallback(async () => {
    const s = await situacaoLiturgia();
    setSit(s);
  }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setCarregando(true);
      try {
        const s = await situacaoLiturgia();
        if (vivo) setSit(s);
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') void recarregarSituacao();
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, [recarregarSituacao]);

  // Próximos eventos da pessoa (inscrição ou ingresso).
  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      setProximos([]);
      return;
    }
    let vivo = true;
    (async () => {
      setCarregandoEventos(true);
      try {
        const ids = new Set<number>();
        const { data: insc } = await supabase
          .from('vw_inscritos')
          .select('evento_id')
          .eq('responsavel_id', user.id);
        ((insc as { evento_id: number }[] | null) ?? []).forEach((r) => ids.add(Number(r.evento_id)));
        const { data: ing } = await supabase.from('vw_meus_ingressos').select('evento_id');
        ((ing as { evento_id: number }[] | null) ?? []).forEach((r) => ids.add(Number(r.evento_id)));
        const lista = [...ids].filter((n) => Number.isFinite(n));
        if (lista.length === 0) {
          if (vivo) setProximos([]);
          return;
        }
        const { data: evs } = await supabase
          .from('eventos')
          .select('id, nome, slug, inicio_em, fim_em, imagem_mobile_url, imagem_desktop_url')
          .in('id', lista);
        const agora = Date.now();
        const ordenados = ((evs as EventoLido[] | null) ?? [])
          .filter((e) => new Date(e.fim_em || e.inicio_em).getTime() >= agora - 86400000)
          .sort((a, b) => new Date(a.inicio_em).getTime() - new Date(b.inicio_em).getTime())
          .slice(0, 2)
          .map<EventoProximo>((e) => ({
            id: e.id,
            nome: e.nome,
            slug: e.slug,
            inicioEm: e.inicio_em,
            capa: e.imagem_mobile_url || e.imagem_desktop_url,
          }));
        if (vivo) setProximos(ordenados);
      } catch {
        if (vivo) setProximos([]);
      } finally {
        if (vivo) setCarregandoEventos(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [pronto, user]);

  async function ativarAvisos() {
    setSalvando(true);
    try {
      await ligarAvisosGerais();
      try {
        await definirLiturgia(true);
      } catch {
        /* segue: a assinatura já está feita; o switch aparece */
      }
      await recarregarSituacao();
      setSit((s) => ({ ...s, ativa: true, assinado: true, liturgia: true }));
      mostrarToast('Pronto! Você recebe o Evangelho todo dia às 6h.', 5000, 'sucesso');
    } catch (e) {
      mostrarToast(mensagemAmigavel(e, 'Não conseguimos ligar os avisos agora.'), 6000, 'erro');
    } finally {
      setSalvando(false);
    }
  }

  async function alternar() {
    if (salvando) return;
    setSalvando(true);
    const alvo = !sit.liturgia;
    try {
      const novo = await definirLiturgia(alvo);
      setSit((s) => ({ ...s, liturgia: novo }));
      mostrarToast(
        novo ? 'Evangelho todo dia às 6h ligado.' : 'Evangelho todo dia desligado.',
        4000,
        novo ? 'sucesso' : 'neutro',
      );
    } catch (e) {
      mostrarToast(mensagemAmigavel(e, 'Ative os avisos neste celular primeiro.'), 6000, 'aviso');
    } finally {
      setSalvando(false);
    }
  }

  const mostrarSwitch = pushOk && !noIphoneSemApp && sit.assinado;

  return (
    <AppScreen>
      <ScreenHeader titulo="Liturgia de hoje" voltar />

      <main className="mx-auto w-full max-w-[720px] px-5 pb-16 pt-4">
        <p className="app-entra text-[15px] font-semibold text-foreground-500">{dataLonga()}</p>

        {/* Leitura de hoje */}
        <Card className="app-entra mt-3 p-5">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-primary-50 text-[24px] text-primary-600">
              <i className="ri-book-open-line" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-[17px] font-extrabold leading-tight text-foreground-950">
                A Palavra de hoje
              </h2>
              <p className="mt-1 text-[14px] leading-relaxed text-foreground-600">
                Leituras, salmo e Evangelho de hoje, na tradução oficial da CNBB.
              </p>
            </div>
          </div>

          <a
            href={LITURGIA_CNBB}
            target="_blank"
            rel="noopener noreferrer"
            className="aperta mt-4 inline-flex h-14 w-full items-center justify-center gap-2 whitespace-nowrap rounded-[18px] bg-primary-500 px-6 text-[15px] font-bold text-white shadow-pbtn transition-colors hover:bg-primary-600"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-external-link-line" aria-hidden />
            </span>
            Ler a liturgia de hoje
          </a>
          <p className="mt-2 text-[12px] text-foreground-400">
            Fonte: Edições CNBB · Igreja em Oração
          </p>
        </Card>

        {/* Hoje no Se Liga */}
        {user && (carregandoEventos || proximos.length > 0) && (
          <section className="app-entra mt-6">
            <h2 className="text-[15px] font-extrabold text-foreground-950">Hoje no Se Liga</h2>
            {carregandoEventos ? (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="h-40 animate-pulse rounded-[16px] bg-background-200/70" />
                <div className="h-40 animate-pulse rounded-[16px] bg-background-200/70" />
              </div>
            ) : (
              <div className="mt-3 grid grid-cols-2 gap-3">
                {proximos.map((ev, i) => (
                  <div key={ev.id} className="app-entra" style={{ animationDelay: `${i * 50}ms` }}>
                    <CardProximoEvento
                      nome={ev.nome}
                      slug={ev.slug}
                      capa={ev.capa}
                      inicioEm={ev.inicioEm}
                      status={{ texto: textoFaltam(ev.inicioEm), tom: 'primary' }}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {!user && pronto && (
          <Card className="app-entra mt-6 p-5">
            <p className="text-[14px] text-foreground-600">
              Entre na sua conta para ver aqui o seu próximo evento.
            </p>
            <SecondaryButton
              cheio
              className="mt-3"
              onClick={() => navigate('/entrar?voltar=%2Fliturgia')}
            >
              Entrar na minha conta
            </SecondaryButton>
          </Card>
        )}

        {/* Aviso do Evangelho */}
        <Card className="app-entra mt-6 p-5">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-accent-100 text-[24px] text-accent-700">
              <i className="ri-sun-line" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-[16px] font-extrabold leading-tight text-foreground-950">
                Evangelho todo dia às 6h
              </h2>
              <p className="mt-0.5 text-[13px] text-foreground-600">
                Um lembrete curto com a liturgia de cada manhã.
              </p>
            </div>

            {mostrarSwitch && (
              <button
                type="button"
                role="switch"
                aria-checked={sit.liturgia}
                aria-label="Evangelho todo dia às 6h"
                disabled={salvando || carregando}
                onClick={alternar}
                className={`aperta flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors disabled:opacity-60 ${
                  sit.liturgia ? 'bg-primary-500' : 'bg-background-300'
                }`}
              >
                <span
                  className={`h-6 w-6 rounded-full bg-white shadow-pcard transition-transform ${
                    sit.liturgia ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            )}
          </div>

          {noIphoneSemApp ? (
            <div className="mt-4">
              <p className="text-[14px] text-foreground-600">
                No iPhone, instale o Se Liga (Compartilhar › Adicionar à Tela de Início) para
                receber o Evangelho todo dia.
              </p>
              <Link
                to="/instalar"
                className="aperta mt-3 inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-[14px] border border-background-200 bg-background-50 px-5 text-[14px] font-semibold text-foreground-800"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-download-2-line" aria-hidden />
                </span>
                Como instalar
              </Link>
            </div>
          ) : !pushOk ? (
            <p className="mt-4 text-[14px] text-foreground-600">
              Este aparelho não recebe avisos do Se Liga.
            </p>
          ) : !sit.assinado ? (
            <div className="mt-4">
              <p className="text-[14px] text-foreground-600">
                Ative os avisos para receber o Evangelho todo dia.
              </p>
              <PrimaryButton
                cheio
                className="mt-3"
                icone="ri-notification-3-line"
                carregando={salvando}
                onClick={ativarAvisos}
              >
                Ativar avisos
              </PrimaryButton>
            </div>
          ) : null}
        </Card>
      </main>
    </AppScreen>
  );
}